<template>
  <div>
    <!-- Edit Hike List Modal -->
    <div class="modal fade" id="editHikeListModal" tabindex="-1" aria-labelledby="editHikeListModalLabel" aria-hidden="true">
      <div class="modal-dialog">
        <div class="modal-content p-3">
          <div class="modal-header">
            <h5 class="modal-title" id="editHikeListModalLabel"><strong>Edit Hike List</strong></h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label for="listName" class="form-label">List Name</label>
              <input type="text" v-model="editForm.name" class="form-control" id="listName" required>
            </div>
            <div class="mb-3">
              <label for="listDescription" class="form-label">Description (optional)</label>
              <textarea v-model="editForm.description" class="form-control" id="listDescription" rows="3"></textarea>
            </div>
            <div class="mb-3">
              <label for="listImages" class="form-label">Images</label>

              <!-- Single drag-to-reorder list of ALL images (existing + newly added)
                   so any photo — including just-added ones — can be reordered, and the
                   first becomes the cover. The previous code only made saved images
                   draggable and kept new ones in a separate, non-draggable grid. -->
              <div v-if="images.length > 0" class="mb-3">
                <small class="text-muted d-block mb-1" style="font-size: 0.7rem;">Drag to reorder &middot; First photo will be the cover</small>
                <div class="thumbnails">
                  <div
                    v-for="(image, index) in images"
                    :key="image.uid"
                    class="thumbnail"
                    :class="{ 'dragging': draggingIndex === index, 'drag-over': dragOverIndex === index }"
                    draggable="true"
                    @dragstart="handleDragStart($event, index)"
                    @dragend="handleDragEnd"
                    @dragover.prevent="handleDragOver($event, index)"
                    @drop.prevent="handleDrop(index)"
                  >
                    <img
                      :src="image.kind === 'existing' ? (image.file.smUrl || image.file.lgUrl) : image.previewUrl"
                      :alt="`photo-${index}`"
                      class="thumbnail-image"
                      style="pointer-events: none;"
                    >
                    <div v-if="(image.id && deletingImageIds.includes(image.id)) || image.status === 'uploading'" class="upload-overlay">
                      <div class="upload-spinner"></div>
                    </div>
                    <template v-else>
                      <div v-if="index === 0" class="cover-badge">Cover photo</div>
                      <button type="button" @click="removeImageItem(image)" class="delete-button">x</button>
                    </template>
                  </div>
                </div>
              </div>

              <!-- New Images Upload -->
              <input
                type="file"
                class="form-control"
                id="listImages"
                accept="image/*"
                multiple
                @change="handleImageSelection"
                ref="fileInput"
              >
              <div class="form-text">Add new photos, or drag the images above to reorder / set the cover.</div>
            </div>
            <div class="form-check mb-3">
              <input type="checkbox" v-model="editForm.publiclyVisible" class="form-check-input" id="publiclyVisible">
              <label class="form-check-label" for="publiclyVisible">Make this list public</label>
            </div>
            <div class="d-flex justify-content-end">
              <button type="button" class="btn btn-secondary me-2" data-bs-dismiss="modal">Cancel</button>
              <button type="button" class="btn btn-primary" @click="updateList" :disabled="updating">
                {{ updating ? 'Saving...' : 'Save Changes' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Hike Modal -->
    <div class="modal fade" id="addHikeModal" tabindex="-1" aria-labelledby="addHikeModalLabel" aria-hidden="true">
      <div class="modal-dialog modal-lg">
        <div class="modal-content p-3">
          <div class="modal-header">
            <h5 class="modal-title" id="addHikeModalLabel"><strong>Add Hike to List</strong></h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="row mb-3">
              <div class="col-12">
                <div class="input-group hero-search mt-2 py-2">
                  <input
                    type="text"
                    v-model="searchQuery"
                    class="form-control search-bar__input pl-3"
                    placeholder="Search for hikes"
                    id="hike-search-input"
                  >
                  <span class="input-group-append d-flex align-items-center">
                    <span class="input-group-text">
                      <span class="material-icons">search</span>
                    </span>
                  </span>
                </div>
              </div>
            </div>

            <!-- Loading state -->
            <div v-if="searching" class="text-center py-4">
              <div class="spinner-border text-light" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
            </div>

            <!-- Results tabs -->
            <div v-if="!searching && searchQuery" class="mb-3">
              <ul class="nav nav-tabs" role="tablist">
                <li class="nav-item m-0" role="presentation">
                  <button
                    class="nav-link active"
                    id="official-tab"
                    data-bs-toggle="tab"
                    data-bs-target="#official"
                    type="button"
                  >
                    HiiKER Hikes ({{ officialHikes.length }})
                  </button>
                </li>
                <li class="nav-item m-0" role="presentation">
                  <button
                    class="nav-link"
                    id="user-tab"
                    data-bs-toggle="tab"
                    data-bs-target="#user"
                    type="button"
                  >
                    My Hikes ({{ userHikes.length }})
                  </button>
                </li>
              </ul>

              <div class="tab-content mt-3 big-list">
                <!-- Official Hikes Tab -->
                <div class="tab-pane fade show active overflow-auto" id="official" style="max-height: 400px;">
                  <div v-if="officialHikes.length === 0" class="text-center text-muted py-4">
                    No official hikes found
                  </div>
                  <div
                    v-for="(hit, index) in officialHikes"
                    :key="hit.document.id"
                    class="result-item cursor-pointer"
                    @click="addHike(hit.document.id)"
                  >
                    <div class="compact-list d-flex gap-4 align-items-center justify-content-between px-3 py-2">
                      <div class="d-flex gap-3">
                        <img
                          :src="hit.document.square_static_map_image_url || require('../../images/placeholder_thumbnailable.jpg')"
                          class="img-fluid rounded w-100 square"
                          alt=""
                          loading="lazy"
                        >

                        <div class="d-flex flex-column">
                          <p class="mb-0"><strong>{{ hit.document.name }}</strong></p>
                          <p class="mb-1">
                            <small class="text-muted">
                              {{ hit.document.region_name }}, {{ hit.document.country_name }}
                            </small>
                          </p>
                          <p class="m-0 text-yellow">
                            <small>
                              <span v-if="hit.document.distance_meters">
                                {{ (hit.document.distance_meters / 1000).toFixed(1) }} km
                              </span>
                              <span v-if="hit.document.distance_meters && hit.document.elevation_gain_meters" class="mx-1">&#183;</span>
                              <span v-if="hit.document.elevation_gain_meters">
                                {{ hit.document.elevation_gain_meters }}m elevation
                              </span>
                            </small>
                          </p>
                        </div>
                      </div>
                      <span class="material-icons add-hover">add_circle</span>
                    </div>
                    <hr v-if="index < officialHikes.length - 1" class="my-2">
                  </div>
                </div>

                <!-- User Hikes Tab -->
                <div class="tab-pane fade overflow-auto" id="user" style="max-height: 400px;">
                  <div v-if="userHikes.length === 0" class="text-center text-muted py-4">
                    No personal hikes found
                  </div>
                  <div
                    v-for="(hike, index) in userHikes"
                    :key="hike.id"
                    class="result-item cursor-pointer"
                    @click="addHike(hike.id)"
                  >
                    <div class="compact-list d-flex gap-4 align-items-center">
                      <img
                        :src="hike.staticMapImageUrl || require('../../images/placeholder_thumbnailable.jpg')"
                        class="img-fluid rounded w-100 square"
                        alt=""
                        loading="lazy"
                      >
                      <div class="d-flex flex-column">
                        <p class="mb-0"><strong>{{ hike.name }}</strong></p>
                        <p class="mb-1">
                          <small class="text-muted">
                            {{ hike.mainRegion?.name }}, {{ hike.mainCountry?.name }}
                          </small>
                        </p>
                        <p class="m-0 text-yellow">
                          <small>
                            <span v-if="hike.distanceMeters">
                              {{ (hike.distanceMeters / 1000).toFixed(1) }} km
                            </span>
                            <span v-if="hike.distanceMeters && hike.elevationGainMeters" class="mx-1">&#183;</span>
                            <span v-if="hike.elevationGainMeters">
                              {{ hike.elevationGainMeters }}m elevation
                            </span>
                          </small>
                        </p>
                      </div>
                    </div>
                    <hr v-if="index < userHikes.length - 1" class="my-2">
                  </div>
                </div>
              </div>
            </div>

            <!-- Empty state -->
            <div v-if="!searching && !searchQuery" class="text-center text-muted py-4">
              <p>Start typing to search for hikes</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch, onMounted } from "vue";
import { TypesenseResponse } from "../../utils/models/TypesenseResponse";
import graph_client from "../../utils/graphql/graph_client";
import { SearchUserHikesSimple } from "../../utils/graphql/hikes-graph";
import { GetHikeList, UpdateHikeList, AddHikeToList } from "../../utils/graphql/hike-list-graph";
import { GenerateDirectUpload } from "../../utils/graphql/comments-graph";
import { DestroyImage } from "../../utils/graphql/image-graph";
import { Modal } from "bootstrap";
import SparkMD5 from "spark-md5";
import { buildImageAttributesWithCover, KeptImage } from "../../utils/buildImageAttributesWithCover";

export default defineComponent({
  props: {
    hikeListSlug: {
      type: String,
      required: true
    }
  },
  setup(props) {
    const hikeList = ref<any>(null);
    const searchQuery = ref("");
    const searching = ref(false);
    const updating = ref(false);
    const adding = ref(false);
    const officialHikes = ref<any[]>([]);
    const userHikes = ref<any[]>([]);
    const fileInput = ref<HTMLInputElement | null>(null);
    // Single ordered list of all images (existing + newly added). The first
    // entry is the cover photo. Each item is { uid, kind: 'existing'|'new', ... }.
    const images = ref<any[]>([]);
    const deletingImageIds = ref<string[]>([]);
    const draggingIndex = ref<number | null>(null);
    const dragOverIndex = ref<number | null>(null);

    const editForm = ref({
      name: "",
      description: "",
      publiclyVisible: false
    });

    const loadHikeList = async () => {
      try {
        const response = await graph_client.request(GetHikeList, {
          obfuscatedSlug: props.hikeListSlug
        });
        hikeList.value = response.hikeList;

        // Populate edit form
        if (hikeList.value) {
          editForm.value = {
            name: hikeList.value.name,
            description: hikeList.value.description || "",
            publiclyVisible: hikeList.value.publiclyVisible
          };
          images.value = (hikeList.value.images || []).map((img: any) => ({
            uid: `existing_${img.id}`,
            kind: 'existing',
            id: img.id,
            file: img.file
          }));
        }
      } catch (error) {
        console.error("Error loading hike list:", error);
      }
    };

    const performSearch = async (query: string) => {
      if (!query || query.trim().length === 0) {
        officialHikes.value = [];
        userHikes.value = [];
        return;
      }

      searching.value = true;

      try {
        // Search official hikes via Typesense
        const filterString = "made_official_at:>0";
        const typesenseResponse = await TypesenseResponse.get(query, filterString, null, 1, 20);
        if (typesenseResponse) {
          officialHikes.value = typesenseResponse.hits;
        } else {
          officialHikes.value = [];
        }

        // Search user's hikes via GraphQL
        if (window.user) {
          const hikesResponse = await graph_client.request(SearchUserHikesSimple, {
            query: query,
            page: 1,
            perPage: 20,
            // Show all hikes here (including completed) so they can be added to lists.
            hideCompletedHikes: false
          });
          userHikes.value = hikesResponse.searchUserHikes || [];
        } else {
          userHikes.value = [];
        }
      } catch (error) {
        console.error("Error searching hikes:", error);
        officialHikes.value = [];
        userHikes.value = [];
      } finally {
        searching.value = false;
      }
    };

    watch(searchQuery, (newVal) => {
      performSearch(newVal);
    });

    const generateClientId = () => {
      if (window.crypto?.randomUUID) {
        return window.crypto.randomUUID();
      }
      return `img_${Math.random().toString(36).slice(2, 10)}`;
    };

    const calculateMD5Base64 = async (file: File): Promise<string> => {
      const buffer = await file.arrayBuffer();
      const spark = new SparkMD5.ArrayBuffer();
      spark.append(buffer);
      const hashHex = spark.end();
      const bytePairs = hashHex.match(/.{2}/g) || [];
      const bytes = new Uint8Array(bytePairs.map((pair) => parseInt(pair, 16)));
      let binary = '';
      bytes.forEach((byte) => {
        binary += String.fromCharCode(byte);
      });
      return window.btoa(binary);
    };

    const uploadImageAndGetSignedId = async (imageEntry: any): Promise<string> => {
      imageEntry.status = 'uploading';

      const checksumBase64 = await calculateMD5Base64(imageEntry.fileObj);

      const uploadResponse = await graph_client.request(GenerateDirectUpload, {
        input: {
          filename: imageEntry.fileObj.name,
          byteSize: imageEntry.fileObj.size,
          contentType: imageEntry.fileObj.type || 'image/jpeg',
          checksumBase64,
          serviceName: window.publicBucketService
        }
      });

      const uploadData = uploadResponse.generateDirectUpload;
      if (!uploadData) {
        throw new Error('Missing upload credentials.');
      }

      const directUploadHeaders = uploadData.headers ? JSON.parse(uploadData.headers) : {};
      const directUploadResult = await fetch(uploadData.uploadUrl, {
        method: 'PUT',
        headers: {
          ...directUploadHeaders,
          'Content-Type': imageEntry.fileObj.type || 'image/jpeg'
        },
        body: imageEntry.fileObj
      });

      if (!directUploadResult.ok) {
        throw new Error(`Upload failed with status ${directUploadResult.status}.`);
      }

      imageEntry.status = 'uploaded';
      return uploadData.signedId;
    };

    const handleImageSelection = (event: Event) => {
      const input = event.target as HTMLInputElement;
      const files = Array.from(input.files || []);

      files.forEach((file) => {
        const clientId = generateClientId();
        const imageEntry: any = {
          uid: clientId,
          kind: 'new',
          fileObj: file,
          clientId,
          signedId: null,
          status: 'pending',
          previewUrl: ''
        };

        const reader = new FileReader();
        reader.onload = (loadEvent) => {
          imageEntry.previewUrl = (loadEvent.target?.result as string) || '';
        };
        reader.readAsDataURL(file);

        // Append to the unified list so new photos are draggable immediately and
        // can be reordered ahead of existing ones (e.g. to become the cover).
        images.value.push(imageEntry);
      });

      // Clear input so the same file can be selected again
      if (input) {
        input.value = '';
      }
    };

    const removeImageItem = async (image: any) => {
      // Newly added (unsaved) image: just drop it from the list.
      if (image.kind === 'new') {
        images.value = images.value.filter((i: any) => i.uid !== image.uid);
        return;
      }

      // Existing image: confirm + delete server-side, then remove locally.
      if (!confirm('Are you sure you want to delete this image?')) {
        return;
      }

      deletingImageIds.value.push(image.id);

      try {
        await graph_client.request(DestroyImage, {
          image: { id: image.id }
        });
        images.value = images.value.filter((i: any) => i.uid !== image.uid);
      } catch (error) {
        console.error("Error deleting image:", error);
        alert("Failed to delete image");
      } finally {
        deletingImageIds.value = deletingImageIds.value.filter(id => id !== image.id);
      }
    };

    // Native HTML5 drag-to-reorder over the unified `images` list. (This is the
    // mechanism that already worked for saved images; vuedraggable reverted the
    // drop here, so we keep native drag and just run it on the merged list.)
    const handleDragStart = (event: DragEvent, index: number) => {
      draggingIndex.value = index;
      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', String(index));
      }
    };

    const handleDragEnd = () => {
      draggingIndex.value = null;
      dragOverIndex.value = null;
    };

    const handleDragOver = (event: DragEvent, index: number) => {
      event.preventDefault();
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'move';
      }
      dragOverIndex.value = index;
    };

    const handleDrop = (dropIndex: number) => {
      if (draggingIndex.value === null || draggingIndex.value === dropIndex) {
        dragOverIndex.value = null;
        return;
      }
      const newImages = [...images.value];
      const [moved] = newImages.splice(draggingIndex.value, 1);
      newImages.splice(dropIndex, 0, moved);
      images.value = newImages;
      draggingIndex.value = null;
      dragOverIndex.value = null;
    };

    const ensureSelectedImagesUploaded = async () => {
      for (const imageEntry of images.value) {
        if (imageEntry.kind !== 'new' || imageEntry.signedId) continue;
        try {
          imageEntry.signedId = await uploadImageAndGetSignedId(imageEntry);
        } catch (error) {
          imageEntry.status = 'pending';
          throw error;
        }
      }
    };

    const updateList = async () => {
      if (updating.value || !hikeList.value) return;

      updating.value = true;

      try {
        await ensureSelectedImagesUploaded();

        const variables: any = {
          id: hikeList.value.id,
          name: editForm.value.name,
          description: editForm.value.description,
          publiclyVisible: editForm.value.publiclyVisible
        };

        // Order matters: the first kept image becomes the cover photo. The unified
        // `images` list preserves the user's drag order across both existing and
        // newly-added photos. The helper marks index 0 as isCover.
        const kept: KeptImage[] = images.value.map((image: any) =>
          image.kind === 'existing'
            ? { kind: 'existing' as const, id: image.id, clientId: image.uid }
            : { kind: 'new-signed' as const, signedId: image.signedId, clientId: image.clientId }
        );

        const allImagesAttributes = buildImageAttributesWithCover(kept, [], {
          generateClientId
        });

        if (allImagesAttributes.length > 0) {
          variables.imagesAttributes = allImagesAttributes;
        }

        const response = await graph_client.request(UpdateHikeList, variables);

        if (response.updateHikeList.errors.length === 0) {
          // Close modal
          const modalElement = document.getElementById('editHikeListModal');
          if (modalElement) {
            const modal = Modal.getInstance(modalElement);
            modal?.hide();
          }

          // Reload page
          window.location.reload();
        } else {
          alert(response.updateHikeList.errors.map((e: any) => e.errors.join(', ')).join('\n'));
        }
      } catch (error) {
        console.error("Error updating hike list:", error);
        alert("Failed to update hike list");
      } finally {
        updating.value = false;
      }
    };

    const addHike = async (hikeId: string) => {
      if (adding.value || !hikeList.value) return;

      adding.value = true;

      try {
        const response = await graph_client.request(AddHikeToList, {
          hikeListId: hikeList.value.id,
          hikeId: hikeId,
          notes: ""
        });

        if (response.addHikeToList.errors.length === 0) {
          // Close modal
          const modalElement = document.getElementById('addHikeModal');
          if (modalElement) {
            const modal = Modal.getInstance(modalElement);
            modal?.hide();
          }

          // Reload page
          window.location.reload();
        } else {
          alert(response.addHikeToList.errors.map((e: any) => e.errors.join(', ')).join('\n'));
        }
      } catch (error) {
        console.error("Error adding hike:", error);
        alert("Failed to add hike to list");
      } finally {
        adding.value = false;
      }
    };

    onMounted(() => {
      loadHikeList();
    });

    return {
      hikeList,
      editForm,
      searchQuery,
      searching,
      updating,
      adding,
      officialHikes,
      userHikes,
      fileInput,
      images,
      deletingImageIds,
      draggingIndex,
      dragOverIndex,
      handleImageSelection,
      removeImageItem,
      handleDragStart,
      handleDragEnd,
      handleDragOver,
      handleDrop,
      updateList,
      addHike
    };
  }
});
</script>

<style scoped>
  .modal-content{
    height: 84vh;
  }

  .result-item{
    cursor: pointer;
  }

  .add-hover:hover{
    color: var(--yellow);
  }

  /* Image drag-to-reorder — copied verbatim from the working
     PlanTrailSaveModal.vue (hike planner "Save" flow) so behaviour matches. */
  .thumbnails {
    white-space: nowrap; /* Prevent line breaks */
    overflow-x: auto; /* Enable horizontal scrolling */
  }

  .thumbnail {
    border: 1px solid #505150;
    padding: 4px;
    border-radius: 5px;
    display: inline-block;
    margin-right: 11px;
    width: 90px;
    height: 90px;
    position: relative;
    cursor: grab;
  }

  .thumbnail.dragging {
    opacity: 0.4;
  }

  .thumbnail.drag-over {
    border-color: #f5dd42;
  }

  .cover-badge {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(245, 221, 66, 0.92);
    color: #17181d;
    font-size: 0.6rem;
    font-weight: 700;
    padding: 3px 0;
    text-align: center;
    pointer-events: none;
    border-radius: 0 0 3px 3px;
  }

  .upload-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 3px;
  }

  .upload-spinner {
    width: 22px;
    height: 22px;
    border: 3px solid rgba(255, 255, 255, 0.3);
    border-top-color: #fff;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .thumbnail-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 3px;
  }

  .delete-button {
    position: absolute;
    top: 0;
    right: 0;
    background: rgb(0 0 0 / 68%);
    color: #fff;
    font-size: 17px;
    padding: 2px 4px;
    border: none;
    cursor: pointer;
    height: 30px;
    width: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 500;
  }
</style>
